import React, { useCallback, type ChangeEvent } from 'react';
import { Form, Input } from 'antd';

import { isRequiredRule } from '../form-field/utils';
import { CurrencyAmountStepRail } from './currency-amount-step-rail';
import {
  changeEventWithSanitizedValue,
  sanitizeCurrencyAmountInput,
} from './utils/sanitize-currency-amount-input';
import { FormFloatingCurrencyItemProps } from './types';

import styles from './styles.module.scss';

export function FormFloatingCurrencyItem({
  name,
  label,
  rules,
  dependencies,
  validateTrigger,
  formItemClassName,
  className,
  disabled,
  withRequiredMark = false,
  placeholder,
  inputMode,
  onChange,
  onBlur,
  'aria-label': ariaLabel,
  withStepButtons = true,
  step = 10,
  stepMin = 0,
  stepMax,
}: FormFloatingCurrencyItemProps): React.ReactElement {
  const getValueFromEvent = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    return sanitizeCurrencyAmountInput(e?.target?.value ?? '');
  }, []);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const sanitized = sanitizeCurrencyAmountInput(e.target.value);
      onChange?.(changeEventWithSanitizedValue(e, sanitized));
    },
    [onChange],
  );

  const notifyProgrammaticAmountChange = useCallback(
    (value: string) => {
      if (!onChange) {
        return;
      }
      const sanitized = sanitizeCurrencyAmountInput(value);
      const synthetic = {
        target: { value: sanitized } as HTMLInputElement,
        currentTarget: { value: sanitized } as HTMLInputElement,
      } as ChangeEvent<HTMLInputElement>;
      onChange(changeEventWithSanitizedValue(synthetic, sanitized));
    },
    [onChange],
  );

  const hasRequired = Array.isArray(rules) && rules.some(isRequiredRule);
  const formItemClass = [styles.formItem, formItemClassName, className]
    .filter(Boolean)
    .join(' ')
    .trim();

  const fieldBodyClass = [styles.fieldBody, withStepButtons && styles.fieldBodyWithSteppers]
    .filter(Boolean)
    .join(' ')
    .trim();

  return (
    <Form.Item className={formItemClass}>
      <div className={fieldBodyClass}>
        <span className={styles.label}>
          {label}
          {hasRequired && withRequiredMark && <span className={styles.requiredMark}> *</span>}
        </span>
        <Form.Item
          name={name}
          rules={rules}
          dependencies={dependencies}
          validateTrigger={validateTrigger}
          getValueFromEvent={getValueFromEvent}
          noStyle
        >
          <Input
            className={styles.currencyControl}
            prefix="$"
            bordered={false}
            disabled={disabled}
            placeholder={placeholder}
            inputMode={inputMode}
            onChange={handleChange}
            onBlur={onBlur}
            aria-label={ariaLabel}
          />
        </Form.Item>
        {withStepButtons && (
          <CurrencyAmountStepRail
            name={name}
            step={step}
            disabled={disabled}
            min={stepMin}
            max={stepMax}
            onAfterValueApplied={onChange ? notifyProgrammaticAmountChange : undefined}
          />
        )}
      </div>
    </Form.Item>
  );
}
